import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiEdit, FiTrash2, FiClock } from 'react-icons/fi';
import DashboardLayout from '../../components/DashboardLayout';
import API from '../../utils/api';
import { getImageUrl, getPlaceholderUrl } from '../../utils/imageUrl';

const ServiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchServiceDetail = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await API.get(`/api/services/${id}`);
      console.log('Service detail data:', response.data);
      setService(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching service detail:', err);
      const errorMessage = err.response?.data?.message || 'Gagal memuat detail layanan';
      setError(errorMessage);
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchServiceDetail();
  }, [fetchServiceDetail]);

  const handleEdit = () => {
    // Navigate back to services page with a query parameter to open edit modal
    navigate(`/admin/services`, { state: { editServiceId: id } });
  };

  const handleDelete = async () => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus layanan ini?')) {
      return;
    }

    try {
      await API.delete(`/api/services/${id}`);
      alert('Layanan berhasil dihapus!');
      navigate('/admin/services');
    } catch (error) {
      console.error('Error deleting service:', error);
      alert('Gagal menghapus layanan: ' + (error.response?.data?.message || error.message));
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(price);
  };

  if (loading) {
    return (
      <DashboardLayout userRole="admin" userName="Admin">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout userRole="admin" userName="Admin">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
        <button
          onClick={() => navigate('/admin/services')}
          className="flex items-center text-primary hover:underline mb-4"
        >
          <FiArrowLeft className="mr-2" /> Kembali ke daftar layanan
        </button>
      </DashboardLayout>
    );
  }

  if (!service) {
    return (
      <DashboardLayout userRole="admin" userName="Admin">
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-6">
          Layanan tidak ditemukan
        </div>
        <button
          onClick={() => navigate('/admin/services')}
          className="flex items-center text-primary hover:underline mb-4"
        >
          <FiArrowLeft className="mr-2" /> Kembali ke daftar layanan
        </button>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userRole="admin" userName="Admin">
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={() => navigate('/admin/services')}
          className="flex items-center text-primary hover:underline"
        >
          <FiArrowLeft className="mr-2" /> Kembali ke daftar layanan
        </button>
        <h1 className="text-2xl font-bold text-secondary">Detail Layanan</h1>
        <div className="flex space-x-2">
          <button
            onClick={handleEdit}
            className="bg-yellow-500 text-white px-4 py-2 rounded-md hover:bg-yellow-600 flex items-center"
          >
            <FiEdit className="mr-2" /> Edit
          </button>
          <button
            onClick={handleDelete}
            className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600 flex items-center"
          >
            <FiTrash2 className="mr-2" /> Hapus
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="flex flex-col lg:flex-row">
          {/* Left: Service Image */}
          <div className="lg:w-1/2">
            <div className="h-80 lg:h-full w-full relative">
              <img
                src={service.image_url ? getImageUrl(service.image_url) : getPlaceholderUrl('No+Image', 600, 400)}
                alt={service.name}
                className="w-full h-full object-cover rounded-lg"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = getPlaceholderUrl('No+Image', 600, 400);
                }}
              />
              <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 py-4 px-6">
                <h2 className="text-white text-3xl font-bold">{service.name}</h2>
              </div>
            </div>
          </div>

          {/* Right: Service Details */}
          <div className="lg:w-1/2 p-6">
            <div className="border-b pb-4 mb-4">
              <div className="flex justify-between mb-3">
                <h3 className="font-bold text-lg text-gray-700">nama produk: {service.name}</h3>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${service.in_stock ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {service.in_stock ? 'Tersedia' : 'Tidak Tersedia'}
                </span>
              </div>
              <div className="flex justify-between items-center mb-3">
                <div className="font-semibold text-2xl text-primary">
                  harga : {formatPrice(service.price || 0)}
                </div>
                <div className="flex items-center text-gray-600">
                  <FiClock className="mr-1" />
                  <span>Estimasi: {service.estimated_time || 0} menit</span>
                </div>
              </div>
              <div className="flex items-center text-gray-600 mb-3">
                <span className="font-medium">Stok tersedia: {service.stock || 0}</span>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="font-bold mb-2 text-gray-700">deskripsi :</h3>
              <p className="text-gray-600 whitespace-pre-line">{service.description || 'Tidak ada deskripsi tersedia'}</p>
            </div>

            <div className="flex justify-between mt-6 pt-4 border-t">
              <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                Kategori: {service.category || 'Umum'}
              </span>
              <div className="flex space-x-2">
                {service.featured && (
                  <span className="text-sm text-blue-500 bg-blue-50 px-3 py-1 rounded-full">
                    Fitur Utama
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ServiceDetail; 